import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import apiClient from '../../../utils/apiClient';
import { Button } from '../../../components/ui/button';
import { Card } from '../../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { Shield, FileText, Activity } from 'lucide-react';

export default function AccessControl() {
  const { orgId } = useParams();
  const [roles, setRoles] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [orgId]);

  const fetchData = async () => {
    try {
      const [rolesRes, policiesRes, logsRes] = await Promise.all([
        apiClient.get(`/enterprise/orgs/${orgId}/roles`),
        apiClient.get(`/enterprise/orgs/${orgId}/policies`),
        apiClient.get(`/enterprise/orgs/${orgId}/audit-logs?limit=50`),
      ]);
      setRoles(rolesRes.data);
      setPolicies(policiesRes.data);
      setAuditLogs(logsRes.data);
    } catch (error) {
      console.error('Failed to fetch access control data:', error);
    } finally {
      setLoading(false);
    }
  };

  const togglePolicy = async (policyId, currentlyEnabled) => {
    try {
      await apiClient.put(`/enterprise/orgs/${orgId}/policies/${policyId}`, {
        enabled: !currentlyEnabled,
      });
      fetchData();
    } catch (error) {
      console.error('Failed to toggle policy:', error);
    }
  };

  return (
    <div className="p-8 space-y-6" data-testid="access-control-page">
      <div>
        <h1 className="font-heading text-3xl font-bold text-gray-900">Access Control</h1>
        <p className="text-gray-600 mt-1">Manage roles, permissions, and security policies</p>
      </div>

      <Tabs defaultValue="roles">
        <TabsList>
          <TabsTrigger value="roles">
            <Shield className="w-4 h-4 mr-2" />
            Roles
          </TabsTrigger>
          <TabsTrigger value="policies">
            <FileText className="w-4 h-4 mr-2" />
            Policies
          </TabsTrigger>
          <TabsTrigger value="audit">
            <Activity className="w-4 h-4 mr-2" />
            Audit Log
          </TabsTrigger>
        </TabsList>

        <TabsContent value="roles" className="space-y-4">
          <Card className="p-6 rounded-lg">
            <h3 className="font-semibold text-gray-900 mb-4">Organization Roles</h3>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Role Name</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Permissions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {roles.length > 0 ? (
                  roles.map((role) => (
                    <TableRow key={role.id}>
                      <TableCell className="font-medium">{role.name}</TableCell>
                      <TableCell className="text-gray-600">
                        {role.description || 'No description'}
                      </TableCell>
                      <TableCell>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          role.isSystem
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-gray-100 text-gray-700'
                        }`}>
                          {role.isSystem ? 'System' : 'Custom'}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm text-gray-600">
                        {role.permissions?.length || 0} permissions
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-8 text-gray-500">
                      No custom roles. Default roles (Owner, Admin, Member) are active.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="policies" className="space-y-4">
          <Card className="p-6 rounded-lg">
            <h3 className="font-semibold text-gray-900 mb-4">Security Policies</h3>
            <div className="space-y-3">
              {policies.length > 0 ? (
                policies.map((policy) => (
                  <div
                    key={policy.id}
                    className="flex items-center justify-between p-4 rounded-lg border border-gray-200"
                  >
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900">{policy.name}</h4>
                      <p className="text-sm text-gray-600 mt-1">{policy.description}</p>
                    </div>
                    <Button
                      variant={policy.enabled ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => togglePolicy(policy.id, policy.enabled)}
                    >
                      {policy.enabled ? 'Enabled' : 'Disabled'}
                    </Button>
                  </div>
                ))
              ) : (
                <p className="text-center py-8 text-gray-500">
                  No policies configured. Policies will appear here once created.
                </p>
              )}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="audit" className="space-y-4">
          <Card className="p-6 rounded-lg">
            <h3 className="font-semibold text-gray-900 mb-4">Audit Trail</h3>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Timestamp</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Target</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {auditLogs.length > 0 ? (
                  auditLogs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell className="text-sm text-gray-600">
                        {new Date(log.timestamp).toLocaleString()}
                      </TableCell>
                      <TableCell className="font-medium">
                        {log.actorUser?.name || 'Unknown'}
                      </TableCell>
                      <TableCell>
                        <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700 font-mono">
                          {log.action}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm text-gray-600">
                        {log.targetType}: {log.targetId.substring(0, 8)}...
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-8 text-gray-500">
                      No audit logs yet
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
